// Player für Aufnahmen und Mailbox-Nachrichten (Runde 7, Agent 7B). Einmal gebaut, von zwei Stellen benutzt: die Lead-Seite (Feed)
// und die Gespräche mit der Anrufliste (Agent 7D). BESITZER ist 7B; 7D benutzt nur diese Schnittstelle und ändert die Datei nicht,
// fehlt ihm etwas, trägt er einen Wunsch in AENDERUNGSWUENSCHE.md ein. Die Eigenschaften (Props) bleiben stabil, neue kommen nur optional dazu.
//
//   <AufnahmePlayer callId="acti_…" dauerS={87} laeuftAbAm="2026-10-24T09:00:00Z" />
//
// `callId` ist die ID der Anruf-Aktivität (`acti_…`) oder des CRM-Anrufs (`call_crm_…`); die Audiodatei kommt vom Server-Proxy
// `GET /api/aufnahmen/{callId}` (API.md 22.2), nie direkt von Close oder Twilio. Die Datei wird erst bei „Abspielen“ geladen.
// Wie in Close: Abspielen/Pause, Position und Spulen, „0:12 / 1:27“, Tempo 1x / 1,5x / 2x, „läuft in 7 T. ab“, ⋯ mit Link kopieren
// und Herunterladen. Es spielt immer nur ein Player: startet einer, hält ein anderer an.
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { aufnahmenApi, aufnahmeUrl, type AufnahmeArt } from "../api/aufnahmen";
import { TL } from "./lead/leadTexte";
import { kopieren } from "../werkzeuge/tasten";
import { Icon } from "./Icon";
import { useMeldung } from "./Meldungen";
import { MenueKnopf } from "./Menue";
import "./aufnahmePlayer.css";

export interface AufnahmePlayerProps {
  callId: string;
  /** "aufnahme" (Standard) oder "voicemail" (Nachricht auf der Mailbox) */
  art?: AufnahmeArt;
  /** Länge in Sekunden, falls schon bekannt (zeigt "Abspielen 1:27" vor dem Laden) */
  dauerS?: number | null;
  /** Zeitpunkt, an dem Close die Aufnahme löscht (7 Tage nach dem Anruf) */
  laeuftAbAm?: string | null;
  /** schmale Darstellung für Tabellenzeilen und Karten */
  kompakt?: boolean;
}

const TEMPI = [1, 1.5, 2] as const;
const EREIGNIS = "crm:aufnahme-spielt";

function dauerText(s: number): string {
  const ganz = Math.max(0, Math.round(s));
  return `${Math.floor(ganz / 60)}:${String(ganz % 60).padStart(2, "0")}`;
}

export function AufnahmePlayer({ callId, art = "aufnahme", dauerS, laeuftAbAm, kompakt }: AufnahmePlayerProps) {
  const meldung = useMeldung();
  const kennung = useId();
  const audio = useRef<HTMLAudioElement>(null);
  const [aktiv, setAktiv] = useState(false);
  const [spielt, setSpielt] = useState(false);
  const [zeit, setZeit] = useState(0);
  const [gesamt, setGesamt] = useState<number | null>(typeof dauerS === "number" && dauerS > 0 ? dauerS : null);
  const [tempo, setTempo] = useState(0);
  const [fehler, setFehler] = useState<string | null>(null);
  const abgelaufen = art === "aufnahme" && !!laeuftAbAm && new Date(laeuftAbAm).getTime() <= Date.now();
  const ablaufText = art === "aufnahme" ? TL.aufnahme.laeuftAb(laeuftAbAm) : null;
  const url = aufnahmeUrl(callId, art);

  // Immer nur ein Player gleichzeitig
  useEffect(() => {
    const andere = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== kennung) audio.current?.pause();
    };
    window.addEventListener(EREIGNIS, andere);
    return () => window.removeEventListener(EREIGNIS, andere);
  }, [kennung]);

  // Beim ersten Klick: Audio-Element ist da, jetzt abspielen (die Bedienung durch den Klick gilt noch)
  useEffect(() => {
    if (!aktiv) return;
    void audio.current?.play().catch(() => undefined);
  }, [aktiv]);

  const umschalten = useCallback(() => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) void a.play().catch(() => setFehler(TL.aufnahme.nichtAbrufbar));
    else a.pause();
  }, []);

  const tempoWechseln = () => {
    const n = (tempo + 1) % TEMPI.length;
    setTempo(n);
    if (audio.current) audio.current.playbackRate = TEMPI[n];
  };

  const spulen = (sekunde: number) => {
    if (audio.current && Number.isFinite(sekunde)) audio.current.currentTime = sekunde;
    setZeit(sekunde);
  };

  const fehlerErmitteln = async () => {
    setSpielt(false);
    try {
      const info = await aufnahmenApi.info(callId, art);
      setFehler(info.verfuegbar ? TL.aufnahme.nichtAbrufbar : (info.grund ?? TL.aufnahme.nichtAbrufbar));
    } catch {
      setFehler(TL.aufnahme.nichtAbrufbar);
    }
  };

  const menue = [
    {
      label: TL.aufnahme.linkKopieren,
      icon: "kopieren" as const,
      onClick: () => void kopieren(`${window.location.origin}${url}`).then((ok) => (ok ? meldung.ok(TL.aufnahme.linkKopiert) : meldung.zeige("fehler", "Kopieren hat nicht geklappt."))),
    },
    {
      label: TL.aufnahme.herunterladen,
      icon: "pfeil-runter" as const,
      onClick: () => {
        const a = document.createElement("a");
        a.href = aufnahmeUrl(callId, art, true);
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      },
    },
  ];
  const titel = art === "voicemail" ? TL.aufnahme.mailbox : TL.aufnahme.abspielen;

  if (abgelaufen && !aktiv) {
    return (
      <span className={`ap ap--aus${kompakt ? " ap--kompakt" : ""}`} title="Close löscht Aufnahmen nach 7 Tagen.">
        <Icon name="play" groesse={12} />
        {TL.aufnahme.abgelaufen}
      </span>
    );
  }

  if (!aktiv) {
    return (
      <span className={`ap ap--ruhe${kompakt ? " ap--kompakt" : ""}`}>
        <button type="button" className="ap__start" title={titel} aria-label={titel} onClick={() => setAktiv(true)}>
          <Icon name="play" groesse={12} />
          <span>{art === "voicemail" ? "Mailbox" : "Aufnahme"}</span>
          {typeof dauerS === "number" && dauerS > 0 ? <span className="ap__dauer">{dauerText(dauerS)}</span> : null}
        </button>
        {ablaufText ? <span className="ap__ablauf">{ablaufText}</span> : null}
      </span>
    );
  }

  return (
    <span className={`ap ap--an${kompakt ? " ap--kompakt" : ""}`} role="group" aria-label={titel}>
      <audio
        ref={audio}
        src={url}
        preload="auto"
        onPlay={() => {
          setSpielt(true);
          setFehler(null);
          window.dispatchEvent(new CustomEvent(EREIGNIS, { detail: kennung }));
          if (audio.current) audio.current.playbackRate = TEMPI[tempo];
        }}
        onPause={() => setSpielt(false)}
        onEnded={() => {
          setSpielt(false);
          setZeit(0);
        }}
        onTimeUpdate={(e) => setZeit(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setGesamt(d);
          e.currentTarget.playbackRate = TEMPI[tempo];
        }}
        onDurationChange={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setGesamt(d);
        }}
        onError={() => void fehlerErmitteln()}
      />
      <button type="button" className="ap__spiel" title={spielt ? TL.aufnahme.pause : titel} aria-label={spielt ? TL.aufnahme.pause : titel} onClick={umschalten}>
        <Icon name={spielt ? "pause" : "play"} groesse={12} />
      </button>
      {fehler ? (
        <span className="ap__fehler" role="alert">
          {fehler}
        </span>
      ) : (
        <>
          <input
            className="ap__leiste"
            type="range"
            min={0}
            max={gesamt ?? Math.max(zeit, 1)}
            step={0.1}
            value={Math.min(zeit, gesamt ?? zeit)}
            aria-label="Position in der Aufnahme"
            onChange={(e) => spulen(Number(e.target.value))}
            style={{ ["--ap-fortschritt" as string]: gesamt ? `${Math.min(100, (zeit / gesamt) * 100)}%` : "0%" }}
          />
          <span className="ap__zeit">
            {dauerText(zeit)}
            {gesamt ? ` / ${dauerText(gesamt)}` : ""}
          </span>
          <button type="button" className="ap__tempo" title={TL.aufnahme.tempo} aria-label={`${TL.aufnahme.tempo}: ${TEMPI[tempo]}x`} onClick={tempoWechseln}>
            {String(TEMPI[tempo]).replace(".", ",")}x
          </button>
        </>
      )}
      {ablaufText && !kompakt ? <span className="ap__ablauf">{ablaufText}</span> : null}
      <MenueKnopf knopfKlasse="symbolknopf symbolknopf--klein" titel={TL.aufnahme.mehr} punkte={menue} ausrichtung="end" breite={200}>
        <Icon name="mehr" groesse={14} />
      </MenueKnopf>
    </span>
  );
}
